/**
 * Rithmo TTS voice catalog (Deepgram Aura-2 personas exposed in the dashboard).
 *
 * @module core/tts/rithmoTtsVoices
 */

export const RITHMO_TTS_VOICE_IDS = [
  'cordelia',
  'callista',
  'selene',
  'electra',
  'mars',
  'neptune',
  'orpheus',
  'zeus',
] as const;

export type RithmoTtsVoiceId = (typeof RITHMO_TTS_VOICE_IDS)[number];

export type RithmoTtsVoiceGender = 'female' | 'male';

export interface RithmoTtsVoiceOption {
  id: RithmoTtsVoiceId;
  label: string;
  gender: RithmoTtsVoiceGender;
  /** Deepgram Aura-2 REST `model` id */
  deepgramModel: string;
  /** ElevenLabs voice UUID */
  elevenlabsVoiceId: string;
}

/** Default ElevenLabs voice ID — all personas map here until individual voices are configured. */
const DEFAULT_ELEVENLABS_VOICE_ID = 'DXFkLCBUTmvXpp2QwZjA';

/** User-facing list (order: female group, then male). */
export const RITHMO_TTS_VOICE_OPTIONS: RithmoTtsVoiceOption[] = [
  { id: 'callista', label: 'Callista', gender: 'female', deepgramModel: 'aura-2-callista-en', elevenlabsVoiceId: DEFAULT_ELEVENLABS_VOICE_ID },
  { id: 'selene', label: 'Selene', gender: 'female', deepgramModel: 'aura-2-selene-en', elevenlabsVoiceId: DEFAULT_ELEVENLABS_VOICE_ID },
  { id: 'electra', label: 'Electra', gender: 'female', deepgramModel: 'aura-2-electra-en', elevenlabsVoiceId: DEFAULT_ELEVENLABS_VOICE_ID },
  { id: 'cordelia', label: 'Cordelia', gender: 'female', deepgramModel: 'aura-2-cordelia-en', elevenlabsVoiceId: DEFAULT_ELEVENLABS_VOICE_ID },
  { id: 'mars', label: 'Mars', gender: 'male', deepgramModel: 'aura-2-mars-en', elevenlabsVoiceId: DEFAULT_ELEVENLABS_VOICE_ID },
  { id: 'neptune', label: 'Neptune', gender: 'male', deepgramModel: 'aura-2-neptune-en', elevenlabsVoiceId: DEFAULT_ELEVENLABS_VOICE_ID },
  { id: 'orpheus', label: 'Orpheus', gender: 'male', deepgramModel: 'aura-2-orpheus-en', elevenlabsVoiceId: DEFAULT_ELEVENLABS_VOICE_ID },
  { id: 'zeus', label: 'Zeus', gender: 'male', deepgramModel: 'aura-2-zeus-en', elevenlabsVoiceId: DEFAULT_ELEVENLABS_VOICE_ID },
];

const DEEPGRAM_BY_ID: Record<string, string> = Object.fromEntries(
  RITHMO_TTS_VOICE_OPTIONS.map((o) => [o.id, o.deepgramModel]),
);

/** Legacy OpenAI-style slugs → Deepgram (non-Rithmo callers). */
export const LEGACY_VOICE_TO_DEEPGRAM: Record<string, string> = {
  cordelia: 'aura-2-cordelia-en',
  verse: 'aura-2-thalia-en',
  alloy: 'aura-2-arcas-en',
  nova: 'aura-2-andromeda-en',
  shimmer: 'aura-2-callista-en',
  echo: 'aura-2-apollo-en',
  onyx: 'aura-2-zeus-en',
  fable: 'aura-2-vesta-en',
};

export function isRithmoTtsVoiceId(value: string): value is RithmoTtsVoiceId {
  return (RITHMO_TTS_VOICE_IDS as readonly string[]).includes(value);
}

export function resolveDeepgramModelForVoice(voice: string, explicitModel?: string): string {
  if (explicitModel?.trim()) return explicitModel.trim();
  const key = voice.toLowerCase();
  return DEEPGRAM_BY_ID[key] ?? LEGACY_VOICE_TO_DEEPGRAM[key] ?? DEEPGRAM_BY_ID.cordelia;
}

/**
 * Map Rithmo / Deepgram persona id to an OpenAI TTS voice (gpt-4o-mini-tts).
 * Used when TTS_PROVIDER=openai but the user picked a Deepgram-only name.
 */
const ELEVENLABS_BY_ID: Record<string, string> = Object.fromEntries(
  RITHMO_TTS_VOICE_OPTIONS.map((o) => [o.id, o.elevenlabsVoiceId]),
);

export function resolveElevenLabsVoiceIdForVoice(voice: string, explicitId?: string): string {
  if (explicitId?.trim()) return explicitId.trim();
  const envId = typeof process !== 'undefined' ? process.env?.ELEVENLABS_VOICE_ID : undefined;
  if (envId?.trim()) return envId.trim();
  const key = voice.toLowerCase();
  return ELEVENLABS_BY_ID[key] ?? DEFAULT_ELEVENLABS_VOICE_ID;
}

export function resolveOpenAiVoiceForPersona(voice: string): string {
  const key = voice.toLowerCase();
  const rithmo: Record<string, string> = {
    cordelia: 'shimmer',
    callista: 'shimmer',
    selene: 'nova',
    electra: 'coral',
    mars: 'onyx',
    neptune: 'echo',
    orpheus: 'fable',
    zeus: 'onyx',
  };
  if (rithmo[key]) return rithmo[key];
  const legacyOpenAi: Record<string, string> = {
    verse: 'nova',
    alloy: 'alloy',
    nova: 'nova',
    shimmer: 'shimmer',
    echo: 'echo',
    onyx: 'onyx',
    fable: 'fable',
  };
  return legacyOpenAi[key] ?? 'shimmer';
}
